import { useContext, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import AddIcon from '@mui/icons-material/Add';
import { toast } from 'react-toastify';
import EditableList from '../EditableList/EditableList';
import AuthContext from '../../context/authContext';
import useMember from '../../hooks/useMember';
import useBranch from '../../hooks/useBranch';
import useUser from '../../hooks/useUser';
import LockIconWrapper from '../components/LockIconWrapper';
import { viewRootTextPrimarySx } from '../../util/chartTheme';
import AddMemberDialog from './AddMemberDialog';

const statusOptions = [
  { value: 'open', label: 'warte auf Bestätigung' },
  { value: 'confirmed', label: 'warte auf Druck' },
  { value: 'printed', label: 'Gedruckt' },
  { value: 'printing', label: 'Druck läuft' },
  { value: 'link_failed', label: 'Verknüpfung fehlgeschlagen' },
  { value: 'print_failed', label: 'Druck fehlgeschlagen' },
  { value: 'reprint_requested', label: 'Nachdruck angefordert' },
];

function MemberManagement() {
  const authCtx = useContext(AuthContext);
  const { requestCardPrint } = useMember();
  const { getBranches } = useBranch();
  const { getMyBranches } = useUser();

  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printMember, setPrintMember] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [printLoading, setPrintLoading] = useState(false);
  const [branchesLoaded, setBranchesLoaded] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  if (!authCtx.checkRight('member:read')) {
    return <Navigate to='/welcome' replace />;
  }

  const loadBranches = async () => {
    if (branchesLoaded) return;
    try {
      const [allBranches, myBranchResult] = await Promise.all([
        getBranches(),
        getMyBranches().catch(() => null),
      ]);
      const myBranchIds = myBranchResult?.branchIds || [];
      const available =
        myBranchIds.length > 0
          ? (allBranches || []).filter(b => myBranchIds.includes(b._id))
          : allBranches || [];
      setBranches(available);
      setBranchesLoaded(true);

      const savedBranch = sessionStorage.getItem('branch');
      if (savedBranch && available.some(b => b._id === savedBranch)) {
        setSelectedBranch(savedBranch);
      } else if (available.length > 0) {
        setSelectedBranch(available[0]._id);
      }
    } catch (err) {
      toast.error('Fehler beim Laden der Standorte');
    }
  };

  const handleOpenPrintDialog = item => {
    setPrintMember(item);
    setPrintDialogOpen(true);
    loadBranches();
  };

  const handleClosePrintDialog = () => {
    setPrintDialogOpen(false);
    setPrintMember(null);
  };

  const handleConfirmPrint = async () => {
    if (!printMember || !selectedBranch) return;
    setPrintLoading(true);
    try {
      await requestCardPrint(printMember._id, selectedBranch);
      toast.success('Kartendruck wurde gestartet');
      handleClosePrintDialog();
    } catch (err) {
      toast.error('Fehler beim Starten des Kartendrucks');
    } finally {
      setPrintLoading(false);
    }
  };

  const getMemberDisplayName = member => {
    if (!member) return '';
    if (member.playerName?.trim()) return member.playerName.trim();
    return `${member.firstName || ''} ${member.lastName || ''}`.trim() || 'Mitglied';
  };

  const canEditMembers =
    authCtx.checkRight('member:update') || authCtx.checkRight('member:update:own');
  const canEditFreePlay = authCtx.checkRight('member:special:freePlay');
  const canCreateMembers = authCtx.checkRight('member:create');

  // Reload the member list so a newly created member shows up immediately.
  const handleMemberCreated = () => {
    window.dispatchEvent(
      new CustomEvent('editableListRefresh', { detail: { resource: 'member' } })
    );
  };

  const memberSchema = useMemo(
    () => {
      const base = {
        firstName: {
          label: 'Vorname',
          type: 'text',
          placeholder: 'Vorname eingeben',
          minWidth: '140px',
        },
        lastName: {
          label: 'Nachname',
          type: 'text',
          placeholder: 'Nachname eingeben',
          minWidth: '140px',
        },
        playerName: {
          label: 'Codename',
          type: 'text',
          placeholder: 'Codename (max. 15 Zeichen)',
          minWidth: '160px',
        },
        cardStatus: {
          label: 'Kartenzustand',
          type: 'select',
          readOnly: true,
          options: statusOptions,
          minWidth: '180px',
        },
        cardNumber: {
          label: 'Kartennummer',
          type: 'text',
          readOnly: true,
          minWidth: '180px',
        },
        cardIDs: {
          label: 'RFID Karten',
          type: 'array',
          readOnly: true,
          minWidth: '220px',
          displayValue: value => {
            if (!Array.isArray(value) || value.length === 0) return '—';
            return value.join(', ');
          },
        },
        branch: {
          label: 'Standort',
          type: 'text',
          readOnly: true,
          minWidth: '160px',
          displayValue: value => {
            if (!value) return '—';
            return value.locationName || value.longName || '—';
          },
        },
        birthDate: {
          label: 'Geburtsdatum',
          type: 'date',
          readOnly: true,
          minWidth: '160px',
        },
      };
      if (canEditFreePlay) {
        base.description = {
          label: 'Kartenbeschreibung (z. B. Mitarbeiterkarte)',
          type: 'text',
          placeholder: 'Beschreibung der Karte',
          minWidth: '200px',
        };
        base.allowedBranches = {
          label: 'Standorte mit Freispiel (immer erlaubt)',
          type: 'multi-select',
          api: {
            itemField: 'allowedBranches',
            idName: '_id',
            resource: 'branch',
            labelField: 'longName',
            valueField: '_id',
            skipSpecial: true,
          },
          placeholder: 'Standorte auswählen',
          minWidth: '220px',
          listDisplayValue: value => {
            if (!Array.isArray(value) || value.length === 0) return '—';
            if (value.length === 1 && value[0] && typeof value[0] === 'object' && value[0].longName) {
              return value[0].longName;
            }
            return `${value.length} Standort(e)`;
          },
        };
      }
      return base;
    },
    [canEditFreePlay]
  );

  return (
    <Box sx={{ ...viewRootTextPrimarySx }}>
      <EditableList
        schema={memberSchema}
        title='Mitglieder verwalten'
        resourceType='member'
        parentIsEditable={canEditMembers}
        renderTopControlsEnd={() =>
          canCreateMembers ? (
            <Button
              variant='contained'
              size='small'
              startIcon={<AddIcon />}
              onClick={() => setAddOpen(true)}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              Mitglied hinzufügen
            </Button>
          ) : null
        }
        options={{
          titleKey: 'playerName',
          disableCreate: true,
          disableDelete: true,
        }}
        visibleColumnKeys={[
          'firstName',
          'lastName',
          'playerName',
          'branch',
          'cardStatus',
          'cardNumber',
          'cardIDs',
        ]}
        renderRowActions={item =>
          item.cardStatus === 'printed' ? (
            <Tooltip title='Karte erneut drucken'>
              <IconButton
                size='small'
                onClick={e => {
                  e.stopPropagation();
                  handleOpenPrintDialog(item);
                }}
                sx={{ borderRadius: 1 }}
              >
                <LockIconWrapper right='member:special:requestCardPrint'>
                  <PrintIcon fontSize='small' />
                </LockIconWrapper>
              </IconButton>
            </Tooltip>
          ) : null
        }
      />
      <Dialog open={printDialogOpen} onClose={handleClosePrintDialog} maxWidth='sm' fullWidth>
        <DialogTitle>Kartendruck</DialogTitle>
        <DialogContent>
          {printMember && (
            <>
              <Typography sx={{ mb: 2, mt: 1 }}>
                {`Möchtest du die Karte für „${getMemberDisplayName(printMember)}" wirklich erneut drucken?`}
              </Typography>
              {branches.length > 1 ? (
                <FormControl fullWidth size='small'>
                  <InputLabel>Standort</InputLabel>
                  <Select
                    value={selectedBranch}
                    label='Standort'
                    onChange={e => setSelectedBranch(e.target.value)}
                  >
                    {branches.map(branch => (
                      <MenuItem key={branch._id} value={branch._id}>
                        {branch.locationName || branch.longName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : branches.length === 1 ? (
                <Typography variant='body2' color='text.secondary'>
                  Standort: {branches[0].locationName || branches[0].longName}
                </Typography>
              ) : (
                <Typography variant='body2' color='error'>
                  Keine Standorte verfügbar.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePrintDialog} disabled={printLoading}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            onClick={handleConfirmPrint}
            disabled={printLoading || !selectedBranch || branches.length === 0}
            startIcon={printLoading ? <CircularProgress size={16} /> : <PrintIcon />}
          >
            Drucken
          </Button>
        </DialogActions>
      </Dialog>
      <AddMemberDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={handleMemberCreated}
      />
    </Box>
  );
}

export default MemberManagement;

